import React, { useRef } from "react";
import { Avatar, Box, Button, Chip, Dialog, IconButton, Tooltip, Typography } from "@mui/material";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { MISSING, toQuickViewFields } from "./candidateQuickView";

const ACCENT = "#E84610";
const BROWN = "#483C3B";
const INK = "#1F1F1F";
const LABEL = "#444444";

const pillSx = {
  height: 32,
  px: 0.5,
  borderRadius: "100px",
  fontSize: "13px",
  color: INK,
  backgroundColor: "#E8E8E8",
};

const publishedPillSx = { ...pillSx, color: "#2E6B3A", backgroundColor: "#D3EFD8" };

const labelSx = { fontSize: "12px", color: LABEL, lineHeight: "20px" };
const valueSx = { fontSize: "14px", color: INK, lineHeight: "20px" };

const Pill = ({ label, sx = pillSx }) =>
  label === MISSING ? <Typography sx={valueSx}>{MISSING}</Typography> : <Chip label={label} sx={sx} />;

const Field = ({ label, children }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, minHeight: 24 }}>
    <Typography sx={{ ...labelSx, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ display: "flex", justifyContent: "flex-end", textAlign: "right", minWidth: 0 }}>
      {typeof children === "string" ? <Typography sx={valueSx}>{children}</Typography> : children}
    </Box>
  </Box>
);

// Talent Pool card variant: label above its value, per the mockup.
const StackedField = ({ label, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5, minWidth: 0 }}>
    <Typography sx={labelSx}>{label}</Typography>
    {typeof children === "string" ? <Typography sx={valueSx}>{children}</Typography> : children}
  </Box>
);

const Section = ({ title, children }) => (
  <Box
    component="section"
    aria-label={title}
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      p: 2,
      border: "1px solid #DDDDDD",
      borderRadius: (t) => `${t.shape.borderRadius}px`,
      flexShrink: 0,
    }}
  >
    {title && (
      <Typography component="h3" sx={{ fontSize: "14px", fontWeight: 600, letterSpacing: "0.02em", color: INK }}>
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

// Quick-view of one search result (see specs/candidate-quick-view.md). Renders
// from the GET /candidates/ row; no fetch. The last candidate is kept while
// the dialog fades out so its content doesn't blank mid-transition. MUI's
// focus trap returns focus to the eye icon that opened it.
const CandidateQuickViewDialog = ({ candidate, onClose }) => {
  const lastCandidate = useRef(null);
  if (candidate) lastCandidate.current = candidate;
  const shown = candidate || lastCandidate.current;
  const f = shown ? toQuickViewFields(shown) : null;

  return (
    <Dialog
      open={Boolean(candidate)}
      onClose={onClose}
      aria-labelledby="candidate-quick-view-name"
      PaperProps={{
        sx: {
          width: 575,
          height: 1010,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 32px)",
          m: 2,
          p: 3,
          gap: 4,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          opacity: 1,
          borderRadius: (t) => `${t.shape.borderRadius}px`,
          backgroundColor: "#FFF",
          boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.30), 0px 2px 6px 2px rgba(0,0,0,0.15)",
        },
      }}
    >
      {f && (
        <>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexShrink: 0 }}>
            <Avatar
              aria-hidden
              sx={{ width: 84, height: 84, flexShrink: 0, backgroundColor: "#EAE7E6", color: BROWN }}
            >
              <PersonOutlineIcon sx={{ fontSize: 44 }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5, pt: 0.5 }}>
              <Typography
                id="candidate-quick-view-name"
                component="h2"
                sx={{
                  fontSize: "24px",
                  lineHeight: "30px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: "#000",
                  overflowWrap: "anywhere",
                }}
              >
                {f.name}
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#666666" }}>{f.primaryRole}</Typography>
              <Typography sx={{ fontSize: "12px", color: LABEL }}>{f.location}</Typography>
            </Box>
            <IconButton aria-label="Close dialog" onClick={onClose} sx={{ color: "#000", mt: -1, mr: -1 }}>
              <HighlightOffOutlinedIcon />
            </IconButton>
          </Box>

          <Box
            data-testid="candidate-quick-view-body"
            sx={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}
          >
            <Section>
              <Field label="System Role">{f.systemRole}</Field>
              <Field label="Contractual Status">
                <Pill label={f.contractualStatus} />
              </Field>
              <Field label="Account Status">
                <Pill label={f.accountStatus} />
              </Field>
            </Section>

            <Section title="CAPABILITY SNAPSHOT">
              <Field label="Primary Role">{f.primaryRole}</Field>
              <Field label="Seniority Level">{f.seniority}</Field>
              <Field label="Secondary Roles">
                {f.secondaryRoles.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 0.5 }}>
                    {f.secondaryRoles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        variant="outlined"
                        sx={{
                          height: 32,
                          borderRadius: "100px",
                          borderColor: "#C4C4C4",
                          color: INK,
                          fontSize: "12px",
                          "& .MuiChip-label": { px: 1.25 },
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  MISSING
                )}
              </Field>
            </Section>

            <Section title="AVAILABILITY & INTENT">
              <Field label="Engagement Preferences">{f.engagementPreferences}</Field>
              <Field label="Side Availability">{f.sideAvailability}</Field>
              <Field label="Notice Period">{f.noticePeriod}</Field>
            </Section>

            <Section title="TALENT POOL & ACTIVITY">
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1.5 }}>
                <StackedField label="Published Status">
                  {f.published ? (
                    <Pill label={f.published.label} sx={f.published.published ? publishedPillSx : pillSx} />
                  ) : (
                    MISSING
                  )}
                </StackedField>
                <StackedField label="Last Sign-in">{f.lastSignIn}</StackedField>
                <StackedField label="Previous Contractual Status">{f.previousContractualStatus}</StackedField>
              </Box>
            </Section>
          </Box>

          <Tooltip title="Coming soon">
            <Box component="span" sx={{ display: "block", flexShrink: 0 }}>
              <Button
                fullWidth
                variant="contained"
                disabled
                sx={{
                  py: 1,
                  borderRadius: "8px",
                  backgroundColor: ACCENT,
                  "&.Mui-disabled": { backgroundColor: "#E8A090", color: "#fff" },
                }}
              >
                EDIT USER
              </Button>
            </Box>
          </Tooltip>
        </>
      )}
    </Dialog>
  );
};

export default CandidateQuickViewDialog;
